import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Lightbulb, ShieldCheck, Users, Zap, MessageCircle } from 'lucide-react';
import PageGlow from "@/components/PageGlow";
import HeroWave from "@/components/HeroWave";
import { Picture } from "@/components/media/Picture";
import { brandMark } from "@/lib/media/case-images";

const GRAD    = '#5048E5';
const fg      = 'hsl(var(--foreground))';
const fgMuted = 'hsl(var(--muted-foreground))';
const surface = 'hsl(var(--card))';
const border  = 'hsl(var(--border))';
const orange  = 'hsl(243 75% 59%)';

const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' as const } },
};
const stagger = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.09 } },
};

const VALUES = [
  {
    Icon: MessageCircle,
    color: orange,
    title: 'Plain English, always',
    body: "We never hide behind jargon. If you don't understand what we're doing or why, we haven't done our job. Every conversation, every report, every recommendation — clear and jargon-free.",
  },
  {
    Icon: ShieldCheck,
    color: orange,
    title: 'You own everything',
    body: "Your website, your domain, your data, your accounts. We set it all up in your name and hand you the keys. No lock-in, no hostage situations — ever.",
  },
  {
    Icon: Lightbulb,
    color: orange,
    title: 'Honest advice over easy sales',
    body: "We'd rather tell you something isn't right for your business than sell you something you don't need. Our reputation is built on results, not invoices.",
  },
  {
    Icon: Zap,
    color: orange,
    title: 'Speed without shortcuts',
    body: "AI-assisted workflows let us move fast — but we never cut corners on quality. You get a professional result in weeks, not months, without paying for wasted time.",
  },
  {
    Icon: Heart,
    color: orange,
    title: 'Small business first',
    body: "We exist specifically for small businesses, tradespeople, and new businesses. Not as a stepping stone to enterprise clients — this is our focus, our passion, and where we do our best work.",
  },
  {
    Icon: Users,
    color: orange,
    title: 'Long-term partnership',
    body: "We're not interested in one-off projects and disappearing. We want to be the digital partner you call first — for years, not just for the launch.",
  },
];

const TEAM = [
  {
    name: 'Morgan Antell',
    role: 'Founder & Lead Consultant',
    bio: "With a background spanning digital marketing, web development, and business operations, Morgan started Digitally Baffled after watching too many small businesses get burned by agencies that overpromised, underdelivered, and left them with nothing they owned. The mission has been the same from day one: make digital transformation genuinely accessible to small businesses.",
    photo: '/__l5e/assets-v1/add148a2-15b7-4723-aaf0-4a214e116c09/team-founder.jpg',
  },
];

// JSON-LD lives in ./about.seo so route head() stays lightweight.

export default function AboutPage() {
  return (
    <>
      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-10 md:py-14" style={{ background: 'hsl(var(--background))' }}>
        <HeroWave />
        <PageGlow tall />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.h1 variants={fadeUp} className="mb-5 text-4xl font-bold md:text-6xl"
              style={{ fontFamily: 'Outfit, sans-serif', color: fg, lineHeight: 1.05 }}>
              We exist because small businesses{' '}
              <span style={{ background: `linear-gradient(135deg, ${GRAD} 0%, hsl(14 100% 57%) 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                deserve better
              </span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg leading-relaxed"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
              Better than confusing tech. Better than overpriced agencies. Better than being made to feel like digital transformation is something only big companies can afford.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── Story ── */}
      <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-5xl px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
            {/* Left — text */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.35 }}>
              <h2 className="mb-6 text-2xl font-bold md:text-3xl" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
                Where it started
              </h2>
              <div className="flex flex-col gap-4 text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                <p>
                  Digitally Baffled was born out of frustration. Not our frustration — the frustration of watching small business owners and tradespeople get let down, overcharged, and left holding websites they didn't own, strategies they didn't understand, and bills they couldn't justify.
                </p>
                <p>
                  The digital world moves fast, and the gap between what big businesses can access and what small businesses can afford has always felt unfair. Agencies built for enterprise clients don't work for a sole trader or a ten-person team. The pricing is wrong, the communication is wrong, and the priorities are wrong.
                </p>
                <p>
                  So we built something different. A managed digital partner for small businesses — the one team that consolidates and runs your entire digital setup (website, client portal, marketing, and every tool your business relies on) as a single ongoing partnership, with transparent pricing, plain-English communication, and a genuine commitment to your long-term success rather than the next invoice.
                </p>
              </div>
            </motion.div>

            {/* Right — chat + mission card */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.35, delay: 0.1 }}
              className="flex flex-col justify-center">
              <div className="rounded-2xl p-7" style={{ background: surface, border: `1px solid ${border}` }}>

                {/* Sender row */}
                <div className="mb-4 flex items-center gap-3">
                  <Picture
                    {...brandMark}
                    alt="Digitally Baffled"
                    className="h-9 w-auto object-contain shrink-0"
                  />
                  <div>
                    <div className="text-sm font-bold leading-tight" style={{ color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Digitally Baffled</div>
                    <div className="text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Our founding principle</div>
                  </div>
                </div>

                {/* Chat bubble */}
                <div className="mb-7 ml-1">
                  <div className="inline-block rounded-2xl rounded-tl-sm px-5 py-4" style={{ background: 'hsla(243,75%,59%,0.10)', border: '1px solid hsla(243,75%,59%,0.2)' }}>
                    <p className="text-base font-semibold leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
                      If you can send an email, you can work with us. The tech is our problem, not yours.
                    </p>
                  </div>
                </div>

                {/* Mission */}
                <div style={{ borderTop: `1px solid ${border}`, paddingTop: '1.5rem' }}>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-widest" style={{ color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Our Mission</div>
                  <p className="text-lg font-bold leading-snug" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
                    To make digital transformation genuinely accessible — and genuinely useful — for every small business.
                  </p>
                </div>

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Values ── */}
      <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-5xl px-6">
          <motion.div>
            <motion.h2 className="mb-10 text-center text-2xl font-bold md:text-3xl" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
              What we stand for
            </motion.h2>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {VALUES.map(({ Icon, color, title, body }) => (
                <motion.div key={title} className="rounded-2xl p-6" style={{ background: surface, border: `1px solid ${border}` }}>
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
                    style={{ background: `color-mix(in srgb, ${color} 12%, transparent)` }}>
                    <Icon size={20} style={{ color }} />
                  </div>
                  <h3 className="mb-2 font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>{title}</h3>
                  <p className="text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>{body}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Team ── */}
      <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-5xl px-6">
          <motion.div>
            <motion.h2 className="mb-10 text-center text-2xl font-bold md:text-3xl" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
              The people behind it
            </motion.h2>
            <div className="mx-auto max-w-2xl">
              {TEAM.map(({ name, role, bio, photo }) => (
                <motion.div key={name} className="flex flex-col gap-6 rounded-2xl p-8 sm:flex-row sm:items-start" style={{ background: surface, border: `1px solid ${border}` }}>
                  {/* Portrait */}
                  <img
                    src={photo}
                    alt={`${name}, ${role} at Digitally Baffled`}
                    width={96}
                    height={96}
                    loading="lazy"
                    fetchPriority="auto"
                    className="h-24 w-24 shrink-0 rounded-2xl object-cover object-center"
                  />
                  {/* Text */}
                  <div>
                    <div className="mb-0.5 text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>{name}</div>
                    <div className="mb-3 text-xs font-semibold uppercase tracking-wider" style={{ color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{role}</div>
                    <p className="text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>{bio}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative overflow-hidden px-6 pb-24 pt-4" style={{ background: 'hsl(var(--background))' }}>
        <HeroWave />
        <div className="mx-auto max-w-3xl relative">
          <motion.div className="flex flex-col items-center gap-5 py-10 text-center">
            <h2 className="text-2xl font-bold md:text-3xl" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
              Ready to work together?
            </h2>
            <p className="max-w-md text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
              Ready to get started? No hard sell, no jargon — just an honest conversation about your business and how we can help.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/get-started"
                className="btn-glow group inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-bold transition-all"
                style={{ background: `linear-gradient(135deg, ${GRAD} 0%, hsl(243 75% 45%) 100%)`, color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Get Started
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link to="/services"
                className="btn-glass-hover group inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-bold transition-all"
                style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--glass-border)', color: 'var(--glass-color)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                See our services
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link to="/case-studies"
                className="btn-glass-hover group inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-bold transition-all"
                style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--glass-border)', color: 'var(--glass-color)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                View case studies
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
            <p className="text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Wondering about cost?{' '}
              <Link to="/services" className="font-semibold underline underline-offset-2 hover:opacity-80" style={{ color: GRAD }}>See services & guide prices →</Link>
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}
